/**
 * Password strength evaluator — kept in sync with backend
 * `routes/auth.py::_check_password_strength`.
 *
 * Rules:
 *   • ≥ 8 characters
 *   • at least one letter AND one digit
 * A password only becomes "Strong" with ≥ 12 chars AND at least one
 * special character (non-alphanumeric).
 *
 * Returns { level (0|1|2|3), label, colorClass, hint, meets }.
 *   - level 0: empty input
 *   - level 1: Weak (fails minimum rules) — cannot submit
 *   - level 2: Good (meets minimum rules)
 *   - level 3: Strong
 */
export function passwordStrength(pwd) {
  if (!pwd) return { level: 0, label: "", colorClass: "", hint: "", meets: false };
  const hasLen8 = pwd.length >= 8;
  const hasLen12 = pwd.length >= 12;
  const hasLetter = /[a-zA-Z]/.test(pwd);
  const hasNumber = /[0-9]/.test(pwd);
  const hasSpecial = /[^a-zA-Z0-9]/.test(pwd);
  const meets = hasLen8 && hasLetter && hasNumber;
  if (!meets) {
    const missing = [];
    if (!hasLen8) missing.push("8+ characters");
    if (!hasLetter) missing.push("a letter");
    if (!hasNumber) missing.push("a number");
    return {
      level: 1,
      label: "Weak",
      colorClass: "bg-red-500",
      hint: `Needs ${missing.join(", ")}`,
      meets: false,
    };
  }
  if (hasLen12 && hasSpecial) {
    return {
      level: 3,
      label: "Strong",
      colorClass: "bg-emerald-600",
      hint: "Great password",
      meets: true,
    };
  }
  return {
    level: 2,
    label: "Good",
    colorClass: "bg-amber-500",
    hint: hasSpecial ? "Add more length for stronger protection" : "Add a symbol for stronger protection",
    meets: true,
  };
}
